import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { isOverdraftMatchingBalance } from "../../../../mobile/ui/screens/utils/account-util";
import { Amount } from "../../../../shared/core/amount/amount";
import { FormInputType } from "../../../../shared/core/data-forms/form-input-types";
import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { logger } from "../../../../shared/core/logging/logger";
import {
  accountManager,
  accountStatementService,
  featuresManager,
  i18NManager,
  savingsManager,
  savingsService,
} from "../../../../shared/core/service/services";
import type { Account } from "../../../../shared/domains/account/account";
import {
  AccountStatus,
  AccountType,
  getAvailableBalance,
  getSettlementBalance,
} from "../../../../shared/domains/account/account";
import { statusColors } from "../../../../shared/domains/account/account-status-labels-and-colors";
import { accountTypeLabelsWeb } from "../../../../shared/domains/account/account-types-labels";
import { retrieveCoOwner } from "../../../../shared/domains/account/account-utils";
import type { AccountStatement } from "../../../../shared/domains/account-statements/account-statement";
import type { FeesStatement } from "../../../../shared/domains/fees-statements/fees-statement";
import type { TaxStatement } from "../../../../shared/domains/savings/savings";
import { isDefined } from "../../../../shared/utils/assert";
import { useObservable } from "../../../../shared/utils/observable";
import type { Paginated, PaginatedTax } from "../../../../shared/utils/pagination";
import { useAsyncEffect } from "../../../../shared/utils/utils";
import { isDateInPast } from "../../../../shared/utils/validate-date";
import { ACCOUNT_DETAIL_MODAL_ID } from "../../../core/modal/modal-id";
import { getAccountProductName } from "../../../utils/get-account-product-name";
import { AccountStatusView } from "../../common/account-status-view";
import { AmountText } from "../../common/amount-text";
import { RoundedModalContainer } from "../../common/modal/rounded-modal-container";
import { ScrollableDiv } from "../../common/modal/scrollable-div";
import { Spinner } from "../../common/spinner";
import { shadows, theme } from "../../styles/theme";
import { GenerateAccountStatementButton } from "../account-transactions/components/generate-account-statement-button";
import { ClipboardInformationRow } from "./components/clipboard-information-row";
import { CollapsableRowsSection } from "./components/collapsable-section";
import { DownloadBankAccountDetails } from "./components/download-bank-details-button";
import { StatementRow } from "./components/statement-row";
import { TaxStatementRow } from "./components/tax-statement-row";

export interface AccountDetailsModalProps {
  account: Account;
  onNameUpdated: (n: string) => void;
  displayedAccountName: string;
}

export const AccountDetailsModal = (props: AccountDetailsModalProps) => {
  const { account, onNameUpdated, displayedAccountName } = props;
  const { formatMessage, formatDate, formatAmount } = useIntl();

  const [authorizedOverdraft, setAuthorizedOverdraft] = useState<string | null>(null);
  const [authorizedOverdraftValue, setAuthorizedOverdraftValue] = useState<number | null>(null);
  const [validityDate, setValidityDate] = useState<string | null>(null);

  const statusMessage = statusMessageKey(account.status);
  const features = useObservable(featuresManager.features);
  const savingsDetails = useObservable(savingsManager.savingDetails);
  const accountIsSavings = account?.type === "SAVINGS";
  const userLanguage = useObservable(i18NManager.localeTag);
  const [setAccountNameEditing] = useState<boolean>(false);
  const [accountNameValue, setAccountNameValue] = useState<string>(displayedAccountName);
  const [success, setSuccess] = useState<string>("");
  const [error, setError] = useState<string>("");

  const productName = getAccountProductName({
    accountIsSavings,
    savingsDetails,
    userLanguage,
    accountTypeLabelsWeb,
    accountType: account?.type,
    formatMessage,
  });

  useAsyncEffect(async () => {
    if (accountIsSavings) {
      await savingsManager.getSavingsDetails(account.id);
    }
    setAccountNameValue(displayedAccountName);
  }, [account, displayedAccountName]);

  useEffect(() => {
    onNameUpdated(accountNameValue);
  }, [accountNameValue]);

  useEffect(() => {
    const fetchOverdraftDetails = async () => {
      try {
        const response = await accountManager.fetchAuthorizedOverdraft(account.id);
        const {
          authorizedOverdraft: fetchedAuthorizedOverdraft,
          validityDate: fetchedValidityDate,
          authorizedOverdraftValue: fetchAuthorizedOverdraftValue,
        } = response;
        setAuthorizedOverdraft(fetchedAuthorizedOverdraft);
        setAuthorizedOverdraftValue(fetchAuthorizedOverdraftValue);
        setValidityDate(fetchedValidityDate);

        if (isDateInPast(fetchedValidityDate)) {
          setAuthorizedOverdraft(null);
        }
      } catch (e) {
        // The overdraft is optional data served by the savings API: tenants without it fail here,
        // and console.error would raise the dev error overlay over a case that is expected.
        logger.debug("AccountDetailsModal", "Failed to fetch overdraft details", e);
      }
    };

    fetchOverdraftDetails();
  }, [account.id]);

  const handleAccountNameUpdate = (name: string) => {
    setSuccess("");
    setError("");
    setAccountNameValue(name);
  };
  const handleSubmitAccountNameUpdate = () => {
    if (accountNameValue !== account.name) {
      accountManager
        .updateAccountName(account.id, accountNameValue)
        .then((res) => {
          setSuccess(formatMessage("accountDetails.success"));
        })
        .catch((e) => {
          setError(e.response.data.message);
        });
    }
  };

  const coOwner = retrieveCoOwner(account);
  const hasMaximumValue = !!savingsDetails?.product?.balanceBoundaries?.maximum?.value;

  return (
    <AccountDetailsModalContainer
      className="account-modal-container"
      contentClassName="content"
      closeButton
      id={ACCOUNT_DETAIL_MODAL_ID}
    >
      <ContentContainer>
        <AccountTypeText>{productName}</AccountTypeText>
        <AccountDetailsModalContent className="scroll-container-without-shadow">
          <StyledAccountStatus account={account} />
          <AccountAvailableBalanceContainer>
            <AccountAvailableBalanceTitle>
              {formatMessage("accountDetails.availableBalanceTitle")}
            </AccountAvailableBalanceTitle>
            <AccountAvailableBalance amount={getAvailableBalance(account)} />
          </AccountAvailableBalanceContainer>
          {statusMessage && (
            <StatusMessage color={statusColors[account.status]}>{formatMessage(statusMessage)}</StatusMessage>
          )}
          {account.type === AccountType.Dedicated && isDefined(account.merchant) ? (
            <DedicatedAccountContainer>
              <DedicatedAccountMessage>{formatMessage("accountDetails.dedicatedMessage")}</DedicatedAccountMessage>
              <DedicatedAccountMerchant>{account.merchant}</DedicatedAccountMerchant>
            </DedicatedAccountContainer>
          ) : null}
          {(error.length > 0 ||
            (!!authorizedOverdraft && isOverdraftMatchingBalance(account, authorizedOverdraftValue))) && (
            <ErrorMessageContainer>
              {error.length > 0 && <ErrorMessage>{error}</ErrorMessage>}
              {!!authorizedOverdraft && isOverdraftMatchingBalance(account, authorizedOverdraftValue) && (
                <ErrorMessage>{formatMessage("accountDetails.reachedOverdraftMessage")}</ErrorMessage>
              )}
            </ErrorMessageContainer>
          )}
          <DedicatedAccountContainer>
            <AccountBalanceTitleContainer>
              <AccountBalanceTitle>{formatMessage("accountDetails.balanceTitle")}</AccountBalanceTitle>
              <AccountBalance amount={getSettlementBalance(account)} />
            </AccountBalanceTitleContainer>
            <AccountBalanceMessage>{formatMessage("accountDetails.balanceMessage")}</AccountBalanceMessage>
          </DedicatedAccountContainer>
          <InformationContainer>
            <StyledClipboardInformationRow
              title={formatMessage("accountDetails.accountName")}
              value={accountNameValue || ""}
              editAction={features.sdaAccountsNameUpdate ? () => setAccountNameEditing(true) : null}
              input={{
                type: FormInputType.Text,
                id: "accountName",
                label: "Account Name",
                value: accountNameValue,
              }}
              onEditChange={(name) => handleAccountNameUpdate(name)}
              onEnterPressed={handleSubmitAccountNameUpdate}
              onBlur={handleSubmitAccountNameUpdate}
              successLabel={success}
              errorLabel={error}
            />
            <StyledClipboardInformationRow title={formatMessage("accountDetails.accountIdTitle")} value={account.id} />
            {coOwner && (
              <StyledClipboardInformationRow
                title={formatMessage("accountDetails.coOwnerTitle")}
                value={coOwner.ownerName}
              />
            )}
            {!accountIsSavings && isDefined(account.iban) && (
              <StyledClipboardInformationRow title={formatMessage("accountDetails.ibanTitle")} value={account.iban} />
            )}
            {!accountIsSavings && isDefined(account.bic) && (
              <StyledClipboardInformationRow title={formatMessage("accountDetails.bicTitle")} value={account.bic} />
            )}
            {!accountIsSavings && authorizedOverdraft !== null && (
              <AuthorizedOverdraftContainer>
                <StyledClipboardInformationRow
                  noCopy
                  title={formatMessage("accountDetails.authorizedOverdraftTitle")}
                  value={String(authorizedOverdraft ?? formatMessage("noDataAvailable"))}
                />
                {validityDate && (
                  <ValidityDateText>
                    {formatMessage("accountDetails.validityDate", {
                      date: formatDate(validityDate, { month: "long", day: "numeric", year: "numeric" }),
                    })}
                  </ValidityDateText>
                )}
              </AuthorizedOverdraftContainer>
            )}
            {accountIsSavings && savingsDetails?.startDate && (
              <StyledClipboardInformationRow
                title={formatMessage("accountDetails.accountStartDate")}
                value={formatDate(savingsDetails.startDate, {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit",
                })}
                noCopy
              />
            )}
            {accountIsSavings && hasMaximumValue && (
              <StyledClipboardInformationRow
                title={formatMessage("accountDetails.maximumBoundaries")}
                value={formatAmount(savingsDetails.product?.balanceBoundaries.maximum)}
              />
            )}
            {isDefined(account.externalData) &&
              Object.entries(account.externalData).map(([title, value], index) => (
                <StyledClipboardInformationRow key={index} title={title} value={value} />
              ))}
          </InformationContainer>
          {accountIsSavings && savingsDetails.initialAmount && (
            <CollapsableRowsSection
              rows={[
                {
                  title: formatMessage("accountDetails.initialAmountLabel"),
                  value: formatAmount(
                    new Amount(savingsDetails.initialAmount.amount, savingsDetails.initialAmount.currency),
                  ),
                },
                {
                  title: formatMessage("accountDetails.endDateLabel"),
                  value: formatDate(savingsDetails.endDate, {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                  }),
                },
                {
                  title: formatMessage("accountDetails.durationLabel"),
                  value: `${savingsDetails.durationValue} ${formatMessage(
                    `accountDetails.monthsLabels.${savingsDetails.cycleFrequency}`,
                  )}`,
                },
              ]}
              accountIsSavings
            />
          )}
          {accountIsSavings &&
            savingsDetails.product?.index?.rate?.value &&
            savingsDetails.product?.index?.rate?.type && (
              <CollapsableRowsSection
                rows={[
                  {
                    title: formatMessage("accountDetails.rateValueLabel"),
                    value: `${savingsDetails.product?.index?.rate?.value} % (${savingsDetails.product.index?.rate?.type})`,
                  },
                  {
                    title: formatMessage("accountDetails.indexCodeLabel"),
                    value: savingsDetails.product?.index?.code,
                  },
                  {
                    title: formatMessage("accountDetails.rateStartDateLabel"),
                    value: formatDate(savingsDetails.product?.index?.rate?.startDate, {
                      day: "2-digit",
                      month: "2-digit",
                      year: "2-digit",
                    }),
                  },
                ]}
                accountIsSavings
              />
            )}
          {accountIsSavings && savingsDetails.accruedInterests && (
            <CollapsableRowsSection
              rows={[
                {
                  title: formatMessage("accountDetails.accruedInterestsLabel"),
                  value: formatAmount(
                    new Amount(savingsDetails.accruedInterests?.amount, savingsDetails.accruedInterests?.currency),
                  ),
                },
                {
                  title: formatMessage("accountDetails.startDateLabel"),
                  value: formatDate(savingsDetails.startDate, {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                  }),
                },
                {
                  title: formatMessage("accountDetails.forecastInterestsLabel"),
                  value: formatAmount(
                    new Amount(savingsDetails.forecastInterests?.amount, savingsDetails.forecastInterests?.currency),
                  ),
                },
                {
                  title: formatMessage("accountDetails.initialAERLabel"),
                  value: `${(+savingsDetails.initialAer * 100).toFixed(2)} %`,
                },
              ]}
              accountIsSavings
            />
          )}
          <div style={{ marginTop: 20 }} />
          {features.sdaAccountView && isDefined(account.iban) && <DownloadBankAccountDetails account={account} />}
          <Statements account={account} />
          {accountIsSavings && <TaxStatements account={account} />}
          {!accountIsSavings && <FeesStatements account={account} />}
        </AccountDetailsModalContent>
      </ContentContainer>
    </AccountDetailsModalContainer>
  );
};

interface StatementsProps {
  account: Account;
}
export const Statements = (props: StatementsProps) => {
  const { account } = props;
  const [statements, setStatements] = useState<Paginated<AccountStatement> | null>(null);
  useEffect(() => {
    accountStatementService.fetchStatements(account.id).then((result) => setStatements(result));
  }, [account.id]);
  const { formatMessage } = useIntl();
  const features = useObservable(featuresManager.features);

  if (statements === null) {
    return (
      <StatementContainer>
        <StatementTitle>{formatMessage("accountsStatements.title")}</StatementTitle>
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      </StatementContainer>
    );
  } else if (statements.items.length > 0) {
    return (
      <StatementContainer>
        <StatementTitle>{formatMessage("accountsStatements.title")}</StatementTitle>
        {features.sdaAccountView ? (
          <StyledGenerateAccountButton>
            <GenerateAccountStatementButton accountId={account.id} />
          </StyledGenerateAccountButton>
        ) : null}
        {statements.items.map((it) => (
          <StyledStatementRow key={it.id} statement={it} account={account} />
        ))}
      </StatementContainer>
    );
  }
  return (
    <StatementContainer>
      <StatementTitle>{formatMessage("accountsStatements.title")}</StatementTitle>
      <NoStatementAvailable>{formatMessage("accountsStatements.emptyStatementList")}</NoStatementAvailable>
    </StatementContainer>
  );
};

interface FeesStatementsProps {
  account: Account;
}
export const FeesStatements = (props: FeesStatementsProps) => {
  const { account } = props;
  const [statements, setStatements] = useState<Paginated<FeesStatement> | null>(null);
  useEffect(() => {
    accountStatementService.fetchFeesStatements(account.id).then((result) => setStatements(result));
  }, [account.id]);
  const { formatMessage } = useIntl();

  if (statements === null) {
    return (
      <StatementContainer>
        <StatementTitle>{formatMessage("feesStatements.title")}</StatementTitle>
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      </StatementContainer>
    );
  } else if (statements.items.length > 0) {
    return (
      <StatementContainer>
        <StatementTitle>{formatMessage("feesStatements.title")}</StatementTitle>
        {statements.items.map((it) => (
          <StyledStatementRow key={it.id} variant="fee" statement={it} account={account} />
        ))}
      </StatementContainer>
    );
  }
  return (
    <StatementContainer>
      <StatementTitle>{formatMessage("feesStatements.title")}</StatementTitle>
      <NoStatementAvailable>{formatMessage("feesStatements.emptyStatementList")}</NoStatementAvailable>
    </StatementContainer>
  );
};

export const TaxStatements = (props: StatementsProps) => {
  const { account } = props;
  const [statements, setStatements] = useState<PaginatedTax<TaxStatement> | null>(null);

  useEffect(() => {
    savingsService.getTaxStatements(account.userId).then((result) => setStatements(result));
  }, [account.id]);
  const { formatMessage } = useIntl();

  if (statements === null) {
    return (
      <StatementContainer>
        <StatementTitle>{formatMessage("taxStatements.title")}</StatementTitle>
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      </StatementContainer>
    );
  } else if (statements.items.length > 0) {
    return (
      <StatementContainer>
        <StatementTitle>{formatMessage("taxStatements.title")}</StatementTitle>
        {statements.items.map((it) => (
          <StyledTaxStatementRow key={it.documentId} variant="fee" statement={it} account={account} />
        ))}
      </StatementContainer>
    );
  }
  return (
    <StatementContainer>
      <StatementTitle>{formatMessage("taxStatements.title")}</StatementTitle>
      <NoStatementAvailable>{formatMessage("taxStatements.emptyStatementList")}</NoStatementAvailable>
    </StatementContainer>
  );
};

const statusMessageKey = (status: AccountStatus) => {
  switch (status) {
    case AccountStatus.Blocked:
      return "accountDetails.accountStatus.blocked";
    case AccountStatus.CreditBlocked:
      return "accountDetails.accountStatus.creditBlocked";
    case AccountStatus.DebitBlocked:
      return "accountDetails.accountStatus.debitBlocked";
    default:
      return undefined;
  }
};
const AccountDetailsModalContainer = styled(RoundedModalContainer)`
  &.account-modal-container {
    margin-top: 10vh;
  }
  .content {
    width: 600px;
  }
`;
const AccountDetailsModalContent = styled(ScrollableDiv)`
  &.scroll-container-without-shadow {
    &::before,
    &::after {
      display: none;
    }
  }
`;
const ContentContainer = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  margin-top: 30px;
  width: 100%;
  height: 710px;
`;

const AccountTypeText = styled.div`
  ${theme.boldText};
  font-size: 1.375rem;
  margin-left: 30px;
  margin-bottom: 20px;
`;

const StyledAccountStatus = styled(AccountStatusView)`
  position: absolute;
  right: 15px;
  top: 0;
`;

const AccountAvailableBalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  align-self: stretch;
`;

const AccountAvailableBalanceTitle = styled.span`
  ${theme.mediumText};
  font-size: 1rem;
  color: #b1b1b1;
`;

const AccountAvailableBalance = styled(AmountText)`
  font-size: 2rem;
  ${theme.boldText};
  margin-top: 5px;
`;

const StatusMessage = styled.div<{ color: string }>`
  color: ${(props) => props.color};
  text-align: center;
  font-size: 0.875rem;
  ${theme.mediumText}
  margin: 30px 30px 0 30px;
`;

const DedicatedAccountContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${shadows.medium};
  margin: 15px 20px 15px 20px;
  padding: 25px 15px 25px 15px;
  border-radius: 13px;
  align-items: center;
`;

const DedicatedAccountMessage = styled.div`
  ${theme.text};
  font-size: 1rem;
  color: #b1b1b1;
  text-align: center;
`;

const DedicatedAccountMerchant = styled.div`
  ${theme.boldText};
  font-size: 0.875rem;
  color: ${theme.mainColor};
`;

const AccountBalanceTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-self: stretch;
  margin-bottom: 10px;
`;
const AccountBalanceTitle = styled.span`
  ${theme.boldText};
`;
const AccountBalance = styled(AmountText)`
  ${theme.boldText};
  color: ${theme.mainColor};
`;
const AccountBalanceMessage = styled.span`
  text-align: center;
  color: #b1b1b1;
  font-size: 0.875rem;
`;

const InformationContainer = styled.div`
  padding: 0 30px 30px 30px;
`;

const AuthorizedOverdraftContainer = styled.div`
  padding: 10px 0 0;
`;

const StyledClipboardInformationRow = styled(ClipboardInformationRow)`
  &:not(:last-of-type) {
    margin-bottom: 15px;
  }
`;

const StatementContainer = styled.div`
  padding: 0 30px 40px 30px;
`;

const StatementTitle = styled.div`
  ${theme.boldText};
  font-size: 1.125rem;
  margin-bottom: 15px;
`;
const StyledStatementRow = styled(StatementRow)`
  padding: 15px 15px 15px 15px;
  ${shadows.medium};
  border-radius: 13px;
  &:not(:last-of-type) {
    margin-bottom: 10px;
  }
`;
const StyledTaxStatementRow = styled(TaxStatementRow)`
  padding: 15px 15px 15px 15px;
  ${shadows.medium};
  border-radius: 13px;
  &:not(:last-of-type) {
    margin-bottom: 10px;
  }
`;

const NoStatementAvailable = styled.span`
  font-size: 0.9375rem;
  color: #b1b1b1;
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const StyledGenerateAccountButton = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ErrorMessageContainer = styled.div`
  padding: 35px 0 10px;
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error[500]};
  text-align: center;
  font-size: 0.875rem;
  ${theme.mediumText}
`;

const ValidityDateText = styled.div`
  ${theme.boldText}
  ${theme.bodyBlackBold}
  margin-top: -10px;
`;
